/**
 * E024-S002-T002: ExcelExtractor
 *
 * Parses Excel files (.xlsx) and extracts data from all sheets.
 * Uses the shared Excel workbook reader and type detection utilities.
 */

import {
  DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT,
  DATABASE_IMPORT_STAGING_ROW_HARD_LIMIT,
} from '@streamlain/database-identity';
import { nanoid } from 'nanoid';

import {
  excelWorkbookReader,
  type WorkbookSheetSnapshot,
} from './excel-workbook-reader';
import type { ColumnType } from './types';
import { columnName, detectColumnType, parseValue } from './utils';

export interface ExcelExtractorOptions {
  file: File;
  selectedSheets?: string[]; // null = all sheets
  hasHeader?: boolean; // default: true
}

export interface SheetInfo {
  name: string;
  rowCount: number;
  columnCount: number;
}

export interface ExcelExtractionResult {
  fileName: string;
  sheets: SheetExtractionResult[];
}

export interface SheetExtractionResult {
  name: string; // Sheet name
  columns: ExcelColumnDef[];
  rows: Record<string, unknown>[];
  rowCount: number;
}

export interface ExcelColumnDef {
  id: string;
  name: string;
  type: ColumnType;
}

export class ExcelExtractor {
  /**
   * Quick analysis without full parsing - returns sheet info
   */
  async analyzeFile(file: File): Promise<SheetInfo[]> {
    const workbook = await this.parseFile(file);
    return workbook.map(sheet => ({
      name: sheet.name,
      rowCount: sheet.rowCount,
      columnCount: sheet.columnCount,
    }));
  }

  /**
   * Full extraction of selected sheets
   */
  async extract(
    options: ExcelExtractorOptions
  ): Promise<ExcelExtractionResult> {
    const { file, selectedSheets, hasHeader = true } = options;

    const workbook = await this.parseFile(file);
    const sheets: SheetExtractionResult[] = [];

    const sheetNames = workbook.map(sheet => sheet.name);
    const sheetsToProcess = selectedSheets || sheetNames;

    for (const sheetName of sheetsToProcess) {
      if (!sheetNames.includes(sheetName)) {
        console.warn(`[ExcelExtractor] Sheet not found: ${sheetName}`);
        continue;
      }

      const worksheet = workbook.find(sheet => sheet.name === sheetName);
      if (!worksheet) continue;

      const result = this.parseSheet(worksheet, sheetName, hasHeader);
      sheets.push(result);
    }

    return {
      fileName: file.name.replace(/\.xlsx$/i, ''),
      sheets,
    };
  }

  private async parseFile(
    file: File
  ): Promise<readonly WorkbookSheetSnapshot[]> {
    const arrayBuffer = await file.arrayBuffer();
    return excelWorkbookReader.read(arrayBuffer, {
      maxRowsToRead: DATABASE_IMPORT_STAGING_ROW_HARD_LIMIT + 1,
      maxColumnsToRead: DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT,
    });
  }

  private parseSheet(
    worksheet: WorkbookSheetSnapshot,
    sheetName: string,
    hasHeader: boolean
  ): SheetExtractionResult {
    const rawData = worksheet.rows;

    if (rawData.length === 0) {
      return {
        name: sheetName,
        columns: [],
        rows: [],
        rowCount: 0,
      };
    }

    // Extract header and data rows
    const headerRow = hasHeader ? (rawData[0] as unknown[]) : null;
    const dataRows = hasHeader ? rawData.slice(1) : rawData;

    const columnCount = Math.min(
      worksheet.columnCount,
      DATABASE_IMPORT_STAGING_COLUMN_HARD_LIMIT
    );

    // Create columns with type detection
    const columns = this.createColumns(
      headerRow,
      columnCount,
      dataRows as unknown[][]
    );

    // Convert rows to records
    const rows = this.convertRows(dataRows as unknown[][], columns);

    return {
      name: sheetName,
      columns,
      rows,
      rowCount: rows.length,
    };
  }

  private createColumns(
    headerRow: unknown[] | null,
    columnCount: number,
    dataRows: unknown[][]
  ): ExcelColumnDef[] {
    const columns: ExcelColumnDef[] = [];

    for (let i = 0; i < columnCount; i++) {
      const headerValue = headerRow?.[i];
      const name =
        headerValue != null && String(headerValue).trim() !== ''
          ? String(headerValue).trim()
          : columnName(i);

      // Get sample values for type detection
      const values = dataRows
        .map(row => row[i])
        .filter(v => v != null && String(v).trim() !== '');

      // Use shared type detection
      const type = detectColumnType(values);

      columns.push({
        id: nanoid(),
        name,
        type,
      });
    }

    return columns;
  }

  private convertRows(
    dataRows: unknown[][],
    columns: ExcelColumnDef[]
  ): Record<string, unknown>[] {
    return dataRows.map(row => {
      const record: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        const rawValue = row[i];
        const value = rawValue != null ? String(rawValue) : '';
        // Use shared parseValue utility
        record[col.id] = parseValue(value, col.type);
      });
      return record;
    });
  }
}
